import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { fetchNews, fetchNewsItem } from "../api/newsApi";

/**
 * Loads news for the active language. Keeps the original API contract
 * (fetchNews -> /api/news?lang=). `limit` optionally trims the result for
 * preview blocks. Returns { news, loading, error }.
 */
export function useNews(limit) {
  const { i18n, t } = useTranslation();
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError("");

    fetchNews(i18n.language)
      .then((items) => {
        if (active) setNews(Array.isArray(items) ? items : []);
      })
      .catch(() => {
        if (active) setError(t("newsLoadError"));
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [i18n.language, t]);

  const data = typeof limit === "number" ? news.slice(0, limit) : news;
  return { news: data, loading, error };
}

/** Loads a single news item by id for the active language. */
export function useNewsItem(id) {
  const { i18n, t } = useTranslation();
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (!id) {
      setLoading(false);
      setNotFound(true);
      return undefined;
    }

    let active = true;
    setLoading(true);
    setError("");
    setNotFound(false);

    fetchNewsItem(id, i18n.language)
      .then((data) => {
        if (!active) return;
        if (!data) {
          setItem(null);
          setNotFound(true);
          return;
        }
        setItem(data);
      })
      .catch(() => {
        if (active) setError(t("newsLoadError"));
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [id, i18n.language, t]);

  return { item, loading, error, notFound };
}
